import { Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';

import {
  GuardedFeature,
  FeatureGuardMode,
  FeatureGuardDropdownItemComponent,
} from '@recruitee/feature-guard';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  DropdownWidth,
  HeadingComponent,
  IconComponent,
  IconColorDirective,
  DropdownItemComponent,
  DropdownHeaderComponent,
  DropdownComponent,
} from '@recruitee/user-interface';
import type { Visibility } from '@recruitee/visibility-types';
import { VisibilityLevel } from '@recruitee/visibility-types';

import { VisibilitySelectLabels } from '../types';

export type VisibilityInfoContext = 'view' | 'access';

export interface VisibilityLabels {
  protected: string;
  public: string;
  private: string;
  subheader: {
    public: string;
    protected: string;
    private: string;
  };
}

@Component({
  selector: 'rt-visibility-selector-dropdown',
  styleUrls: ['visibility-selector-dropdown.component.less'],
  templateUrl: 'visibility-selector-dropdown.component.html',
  standalone: true,
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconColorDirective,
    IconComponent,
    HeadingComponent,
    FeatureGuardDropdownItemComponent,
    TranslatePipe,
  ],
})
export class VisibilitySelectorDropdownComponent {
  public i18n = inject(I18nService);

  @Input() public visibility: Visibility;
  @Input() public hasOnlyMeLevel: boolean = true;
  @Input() public hasSelectTeamMembers: boolean = true;
  @Input() public infoContext: VisibilityInfoContext;
  @Input() public labels: VisibilitySelectLabels['dropdown'] = this.getLabels();

  @Output() public changeVisibilityLevel: EventEmitter<VisibilityLevel> = new EventEmitter();
  @Output() public toggleDropdown: EventEmitter<void> = new EventEmitter();

  public FeatureGuardMode: typeof FeatureGuardMode = FeatureGuardMode;
  public GuardedFeature: typeof GuardedFeature = GuardedFeature;
  public readonly DropdownWidth = DropdownWidth;

  public readonly VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.infoContext && this.infoContext && !this.labels) {
      this.labels = this.getLabels(this.infoContext);
    }
  }

  public ngOnInit() {
    if (!this.labels) {
      this.labels = this.getLabels(this.infoContext);
    }
  }

  private getLabels(context?: VisibilityInfoContext) {
    switch (context) {
      case 'access':
        return {
          header: {
            protected: this.i18n.translate('app.visibility.access_by'),
            public: this.i18n.translate('app.visibility.access_by_everyone'),
            private: this.i18n.translate('app.visibility.access_by_me'),
          },
          subheader: {
            public: this.i18n.translate('app.visibility.all_team_members_access'),
            protected: this.i18n.translate('app.visibility.selected_team_members_access'),
            private: this.i18n.translate('app.visibility.only_you_access'),
          },
        };

      default:
      case 'view':
        return {
          header: {
            public: this.i18n.translate('app.visibility.selector.public.heading'),
            protected: this.i18n.translate('app.visibility.selector.protected.heading'),
            private: this.i18n.translate('app.visibility.selector.private.heading'),
          },
          subheader: {
            public: this.i18n.translate('app.visibility.selector.public.subheading'),
            protected: this.i18n.translate('app.visibility.selector.protected.subheading'),
            private: this.i18n.translate('app.visibility.selector.private.subheading'),
          },
        };
    }
  }
}
